import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { flowRight } from 'lodash';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  appLinkOutAction,
  fetchMyRaasAssessmentsAction,
  trackNewPageLoadAction
} from '../../components/app/app-state-actions.utilities';
import {
  RaasAssessmentWithAssignmentDto,
  RecommendationGenerationStatusDto,
  StatusWithDueDate
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import {
  ELSDataTable,
  ELSTextBox
} from '../../components/els.components';
import { StatusPill } from '../../components/status-pill/StatusPill.component';
import { formatSubmittedDate } from '../remediation-student-activity/remediation-student-activity.utilities';

// Temporary PLP eval workaround (FLOR-4070): lists the launched user's PLPs so eval SMEs can open them without Launchpad

const FAILED_STATUSES: RecommendationGenerationStatusDto[] = [
  RecommendationGenerationStatusDto.ERROR,
  RecommendationGenerationStatusDto.CANCELLED_TIMED_OUT,
  RecommendationGenerationStatusDto.CANCELLED_ADMIN_REQUEST,
  RecommendationGenerationStatusDto.INVALID_ASSIGNMENT_GOALS,
  RecommendationGenerationStatusDto.NOT_FOUND
];

const renderStatus = (status: RecommendationGenerationStatusDto): JSX.Element => {
  if (status === RecommendationGenerationStatusDto.COMPLETED) {
    return <StatusPill status={StatusWithDueDate.COMPLETED_PASSED}>Ready</StatusPill>;
  }
  if (FAILED_STATUSES.includes(status)) {
    return <StatusPill status={StatusWithDueDate.COMPLETED_FAILED}>Failed</StatusPill>;
  }
  return <StatusPill status={StatusWithDueDate.IN_PROGRESS}>In progress</StatusPill>;
};

const renderCreated = (row: RaasAssessmentWithAssignmentDto): JSX.Element => {
  return <span className="u-els-nowrap">{formatSubmittedDate(row.raasAssessment.createdAt)}</span>;
};

type PlpListState = {
  raasAssessments: RaasAssessmentWithAssignmentDto[];
  hasError: boolean;
  hasLaunchError: boolean;
}

export const PlpListComponent = (): JSX.Element => {

  const {
    appState,
    appStateRef,
    setAppState
  } = useContext(AppContext);

  const [searchText, setSearchText] = useState('');
  const [state, setState] = useState<PlpListState>({
    raasAssessments: null,
    hasError: false,
    hasLaunchError: false
  });

  useEffect(() => {
    fetchMyRaasAssessmentsAction({ appState, appStateRef, setAppState })
      .then((raasAssessments) => {
        setState({ raasAssessments, hasError: false, hasLaunchError: false });
      })
      .catch(() => {
        setState({ raasAssessments: null, hasError: true, hasLaunchError: false });
      })
      .finally(() => {
        trackNewPageLoadAction({ appState, appStateRef, setAppState });
      });
  }, []);

  const openPlp = (row: RaasAssessmentWithAssignmentDto) => {
    setState((prevState) => ({ ...prevState, hasLaunchError: false }));
    appLinkOutAction({
      appState,
      appStateRef,
      setAppState,
      config: {
        app: Application.REMEDIATION,
        action: AppAction.LAUNCH,
        altSrcApp: Application.REMEDIATION,
        body: { assessmentId: row.raasAssessment.studentStudyEolsAssessmentId },
        // per-launch link hash so concurrent new-tab launches don't overwrite the shared link-ID cookie
        includeLinkHash: true
      },
      isNewTab: true
    }).catch(() => {
      setState((prevState) => ({ ...prevState, hasLaunchError: true }));
    });
  };

  const renderOpenButton = (row: RaasAssessmentWithAssignmentDto) => (
    <button
      type="button"
      className="u-els-debuttonize u-els-anchorize"
      onClick={() => openPlp(row)}
    >
      Open
    </button>
  );

  const renderContent = () => {
    if (state.hasError) {
      return <p className="u-els-color-warn">Unable to load your PLPs. Please refresh the page to try again.</p>;
    }
    if (!state.raasAssessments) {
      return null;
    }
    if (!state.raasAssessments.length) {
      return <p>No PLPs found.</p>;
    }
    const searchTerm = searchText.trim();
    const filteredAssessments = state.raasAssessments.filter((row) => String(row.raasAssessment.id).includes(searchTerm));
    return (
      <>
        <div className="o-els-container">

          <ELSTextBox
            value={searchText}
            changeHandler={(e, value) => setSearchText(value)}
          >
            Search by PLP ID
          </ELSTextBox>
        </div>
        {state.hasLaunchError && <p className="u-els-color-warn">Unable to open the PLP. Please try again.</p>}
        {!filteredAssessments.length ? <p>No PLPs match your search.</p> : (
          <ELSDataTable
            data={filteredAssessments}
            cssModifier="c-els-table--vertical-dividers"
            columns={[
              {
                header: 'PLP ID',
                customRender: (row: RaasAssessmentWithAssignmentDto) => row.raasAssessment.id
              },
              {
                header: 'Assignment',
                customRender: (row: RaasAssessmentWithAssignmentDto) => row.remediationAssignment?.eolsAssignment?.title || ''
              },
              {
                header: 'Created',
                customRender: (row: RaasAssessmentWithAssignmentDto) => renderCreated(row)
              },
              {
                header: 'Status',
                customRender: (row: RaasAssessmentWithAssignmentDto) => renderStatus(row.raasAssessment.recGenerationStatus)
              },
              {
                header: 'Open',
                customRender: (row: RaasAssessmentWithAssignmentDto) => renderOpenButton(row)
              }
            ]}
          />
        )}
      </>
    );
  };

  return (
    <div className="u-els-padding-3x">
      <h1>My PLPs</h1>
      <div className="o-els-container">
        {renderContent()}
      </div>
    </div>
  );
};

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'My PLPs' }),
  withPageLoader,
);

const PlpList = enhance(PlpListComponent) as React.FC;
export default PlpList;
